import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { BarChart as BarChartIcon, TrendingUp, Users, Clock, MessageSquare, Calendar, Megaphone, Download, FileText, FileSpreadsheet, Radio, RefreshCw } from "lucide-react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

interface AnalyticsData {
  totalMeetings: number;
  totalParticipants: number;
  totalMessages: number;
  avgDuration: number;
  avgParticipants: number;
  todayMeetings: number;
  weekMeetings: number;
  monthMeetings: number;
  recentMeetings: Array<{
    id: string;
    title: string;
    duration_minutes: number;
    total_participants: number;
    total_messages: number;
    ended_at: string;
  }>;
  dailyStats: Array<{
    date: string;
    meetings: number;
    participants: number;
  }>;
}

interface WebinarMetrics {
  totalWebinars: number;
  avgAttendance: number;
  avgHandRaises: number;
  peakHourLabel: string;
  peakAttendance: number;
}

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'all'>('week');
  const [webinarMetrics, setWebinarMetrics] = useState<WebinarMetrics | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();

  // Analytics is now open for all users
  const isAllowed = true;

  // Fetch analytics data
  const fetchAnalyticsData = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    await fetchAnalytics();
    setLastUpdated(new Date());
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (isAllowed) {
      fetchAnalyticsData();
    }
  }, [dateRange, isAllowed]);

  // Real-time subscription to meeting_analytics table
  useEffect(() => {
    if (!autoRefresh || !isAllowed) return;

    console.log('[Analytics] Setting up real-time subscription...');
    
    const channel = supabase
      .channel('analytics-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meeting_analytics'
        },
        (payload) => {
          console.log('[Analytics] Real-time update received:', payload.eventType);
          fetchAnalyticsData(true);
          toast({
            title: "Analytics updated",
            description: `New ${payload.eventType} event received`,
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meetings'
        },
        (payload) => {
          console.log('[Analytics] Meetings table update:', payload.eventType);
          fetchAnalyticsData(true);
        }
      )
      .subscribe((status) => {
        console.log('[Analytics] Subscription status:', status);
      });

    return () => {
      console.log('[Analytics] Removing real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [autoRefresh, isAllowed, dateRange]);

  // Manual refresh handler
  const handleManualRefresh = () => {
    fetchAnalyticsData(true);
    toast({ title: "Analytics refreshed" });
  };


  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startDate: Date | null = null;

      if (dateRange === 'week') {
        startDate = subDays(now, 7);
      } else if (dateRange === 'month') {
        startDate = subDays(now, 30);
      }

      // Fetch all analytics data
      const { data: allAnalytics, error: analyticsError } = await supabase
        .from("meeting_analytics")
        .select("*")
        .order("ended_at", { ascending: false });

      if (analyticsError) throw analyticsError;

      // Filter by date range if needed
      const filteredAnalytics = startDate
        ? allAnalytics?.filter(a => a.ended_at && new Date(a.ended_at) >= startDate) || []
        : allAnalytics || [];

      // Calculate statistics
      const totalMeetings = filteredAnalytics.length;
      const totalParticipants = filteredAnalytics.reduce((sum, m) => sum + (m.total_participants || 0), 0);
      const totalMessages = filteredAnalytics.reduce((sum, m) => sum + (m.total_messages || 0), 0);
      const totalDuration = filteredAnalytics.reduce((sum, m) => sum + (m.duration_minutes || 0), 0);
      const avgDuration = totalMeetings > 0 ? Math.round(totalDuration / totalMeetings) : 0;
      const avgParticipants = totalMeetings > 0 ? Math.round(totalParticipants / totalMeetings) : 0;

      // Today's meetings
      const todayStart = startOfDay(now);
      const todayEnd = endOfDay(now);
      const todayMeetings = allAnalytics?.filter(a => 
        a.ended_at && 
        new Date(a.ended_at) >= todayStart && 
        new Date(a.ended_at) <= todayEnd
      ).length || 0;

      // This week's meetings
      const weekStart = subDays(now, 7);
      const weekMeetings = allAnalytics?.filter(a => 
        a.ended_at && new Date(a.ended_at) >= weekStart
      ).length || 0;

      // This month's meetings
      const monthStart = subDays(now, 30);
      const monthMeetings = allAnalytics?.filter(a => 
        a.ended_at && new Date(a.ended_at) >= monthStart
      ).length || 0;

      // Get meeting titles for recent meetings
      const meetingIds = filteredAnalytics.map(a => a.meeting_id);
      const { data: meetingsData } = meetingIds.length > 0
        ? await supabase
            .from("meetings")
            .select("id, title, is_webinar_mode, scheduled_start")
            .in("id", meetingIds)
        : { data: [] };

      const meetingTitles = new Map((meetingsData || []).map((m: any) => [m.id, m.title || 'Untitled Meeting']));
      const webinarMeetingIds = new Set((meetingsData || []).filter((m: any) => m.is_webinar_mode).map((m: any) => m.id));

      const recentMeetings = filteredAnalytics.slice(0, 10).map(a => ({
        id: a.meeting_id,
        title: meetingTitles.get(a.meeting_id) || 'Untitled Meeting',
        duration_minutes: a.duration_minutes || 0,
        total_participants: a.total_participants || 0,
        total_messages: a.total_messages || 0,
        ended_at: a.ended_at || ''
      }));

      const webinarMetricsPayload: WebinarMetrics = {
        totalWebinars: webinarMeetingIds.size,
        avgAttendance: 0,
        avgHandRaises: 0,
        peakHourLabel: 'N/A',
        peakAttendance: 0
      };

      if (webinarMeetingIds.size > 0) {
        const webinarAnalytics = filteredAnalytics.filter(a => webinarMeetingIds.has(a.meeting_id));
        if (webinarAnalytics.length > 0) {
          const totalAttendance = webinarAnalytics.reduce((sum, a) => sum + (a.total_participants || 0), 0);
          webinarMetricsPayload.avgAttendance = Math.round(totalAttendance / webinarAnalytics.length);

          const { data: handRaiseRows } = await supabase
            .from('raised_hands')
            .select('meeting_id')
            .in('meeting_id', Array.from(webinarMeetingIds));

          const totalHandRaises = handRaiseRows?.length || 0;
          webinarMetricsPayload.avgHandRaises = Math.round(totalHandRaises / webinarAnalytics.length);

          const hourBuckets = new Map<string, number>();
          webinarAnalytics.forEach(a => {
            if (a.ended_at) {
              const hourLabel = format(new Date(a.ended_at), 'haaa');
              hourBuckets.set(hourLabel, (hourBuckets.get(hourLabel) || 0) + (a.total_participants || 0));
            }
          });

          hourBuckets.forEach((count, hour) => {
            if (count > webinarMetricsPayload.peakAttendance) {
              webinarMetricsPayload.peakAttendance = count;
              webinarMetricsPayload.peakHourLabel = hour;
            }
          });
        }
      }

      // Daily stats for chart
      const dailyStatsMap = new Map<string, { meetings: number; participants: number }>();
      
      for (let i = dateRange === 'month' ? 29 : 6; i >= 0; i--) {
        const date = format(subDays(now, i), 'yyyy-MM-dd');
        dailyStatsMap.set(date, { meetings: 0, participants: 0 });
      }

      filteredAnalytics.forEach(a => {
        if (a.ended_at) {
          const date = format(new Date(a.ended_at), 'yyyy-MM-dd');
          if (dailyStatsMap.has(date)) {
            const stats = dailyStatsMap.get(date)!;
            stats.meetings += 1;
            stats.participants += a.total_participants || 0;
          }
        }
      });

      const dailyStats = Array.from(dailyStatsMap.entries()).map(([date, stats]) => ({
        date,
        ...stats
      }));

      setAnalytics({
        totalMeetings,
        totalParticipants,
        totalMessages,
        avgDuration,
        avgParticipants,
        todayMeetings,
        weekMeetings,
        monthMeetings,
        recentMeetings,
        dailyStats
      });
      setWebinarMetrics(webinarMetricsPayload);
    } catch (error: any) {
      console.error("Error fetching analytics:", error);
      setWebinarMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <BarChart className="h-8 w-8 animate-pulse text-primary" />
      </div>
    );
  }

  // Export to CSV
  const exportToCSV = () => {
    if (!analytics) return;

    const dateRangeLabel = dateRange === 'week' ? 'Last 7 Days' : dateRange === 'month' ? 'Last 30 Days' : 'All Time';
    
    // Summary data
    let csvContent = "Meeting Analytics Report\n";
    csvContent += `Date Range: ${dateRangeLabel}\n`;
    csvContent += `Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}\n\n`;
    
    csvContent += "Summary Statistics\n";
    csvContent += "Metric,Value\n";
    csvContent += `Total Meetings,${analytics.totalMeetings}\n`;
    csvContent += `Total Participants,${analytics.totalParticipants}\n`;
    csvContent += `Total Messages,${analytics.totalMessages}\n`;
    csvContent += `Average Duration (min),${analytics.avgDuration}\n`;
    csvContent += `Average Participants,${analytics.avgParticipants}\n`;
    csvContent += `Today's Meetings,${analytics.todayMeetings}\n`;
    csvContent += `This Week's Meetings,${analytics.weekMeetings}\n`;
    csvContent += `This Month's Meetings,${analytics.monthMeetings}\n\n`;

    // Webinar metrics if available
    if (webinarMetrics && webinarMetrics.totalWebinars > 0) {
      csvContent += "Webinar Metrics\n";
      csvContent += "Metric,Value\n";
      csvContent += `Total Webinars,${webinarMetrics.totalWebinars}\n`;
      csvContent += `Avg Attendance,${webinarMetrics.avgAttendance}\n`;
      csvContent += `Avg Hand Raises,${webinarMetrics.avgHandRaises}\n`;
      csvContent += `Peak Hour,${webinarMetrics.peakHourLabel}\n`;
      csvContent += `Peak Attendance,${webinarMetrics.peakAttendance}\n\n`;
    }

    // Recent meetings
    csvContent += "Recent Meetings\n";
    csvContent += "Title,Duration (min),Participants,Messages,Ended At\n";
    analytics.recentMeetings.forEach(m => {
      csvContent += `"${m.title}",${m.duration_minutes},${m.total_participants},${m.total_messages},"${m.ended_at ? format(new Date(m.ended_at), 'yyyy-MM-dd HH:mm') : ''}"\n`;
    });

    csvContent += "\nDaily Activity\n";
    csvContent += "Date,Meetings,Participants\n";
    analytics.dailyStats.forEach(s => {
      csvContent += `${s.date},${s.meetings},${s.participants}\n`;
    });

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `analytics-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();

    toast({ title: "CSV exported successfully" });
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!analytics) return;

    const dateRangeLabel = dateRange === 'week' ? 'Last 7 Days' : dateRange === 'month' ? 'Last 30 Days' : 'All Time';
    const doc = new jsPDF();

    // Title
    doc.setFontSize(20);
    doc.text("Meeting Analytics Report", 14, 22);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Date Range: ${dateRangeLabel} | Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`, 14, 30);

    // Summary statistics table
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text("Summary Statistics", 14, 42);

    autoTable(doc, {
      startY: 46,
      head: [['Metric', 'Value']],
      body: [
        ['Total Meetings', String(analytics.totalMeetings)],
        ['Total Participants', String(analytics.totalParticipants)],
        ['Total Messages', String(analytics.totalMessages)],
        ['Average Duration', `${analytics.avgDuration} min`],
        ['Average Participants', String(analytics.avgParticipants)],
        ["Today's Meetings", String(analytics.todayMeetings)],
        ["This Week's Meetings", String(analytics.weekMeetings)],
        ["This Month's Meetings", String(analytics.monthMeetings)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [124, 58, 237] },
    });

    // Webinar metrics if available
    let currentY = (doc as any).lastAutoTable.finalY + 10;

    if (webinarMetrics && webinarMetrics.totalWebinars > 0) {
      doc.setFontSize(14);
      doc.text("Webinar Metrics", 14, currentY);

      autoTable(doc, {
        startY: currentY + 4,
        head: [['Metric', 'Value']],
        body: [
          ['Total Webinars', String(webinarMetrics.totalWebinars)],
          ['Avg Attendance', String(webinarMetrics.avgAttendance)],
          ['Avg Hand Raises', String(webinarMetrics.avgHandRaises)],
          ['Peak Hour', webinarMetrics.peakHourLabel],
          ['Peak Attendance', String(webinarMetrics.peakAttendance)],
        ],
        theme: 'striped',
        headStyles: { fillColor: [124, 58, 237] },
      });

      currentY = (doc as any).lastAutoTable.finalY + 10;
    }

    // Recent meetings table
    if (analytics.recentMeetings.length > 0) {
      doc.setFontSize(14);
      doc.text("Recent Meetings", 14, currentY);

      autoTable(doc, {
        startY: currentY + 4,
        head: [['Meeting', 'Duration', 'Participants', 'Messages', 'Ended']],
        body: analytics.recentMeetings.map(m => [
          m.title.substring(0, 30),
          `${m.duration_minutes}m`,
          String(m.total_participants),
          String(m.total_messages),
          m.ended_at ? format(new Date(m.ended_at), 'MM/dd HH:mm') : '-'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [124, 58, 237] },
        columnStyles: {
          0: { cellWidth: 50 },
        },
      });
    }

    // Save
    doc.save(`analytics-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);

    toast({ title: "PDF exported successfully" });
  };

  if (!analytics) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls Bar */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        {/* Date Range Filter */}
        <div className="flex gap-2">
          <Button
            variant={dateRange === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange('week')}
          >
            Last 7 Days
          </Button>
          <Button
            variant={dateRange === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange('month')}
          >
            Last 30 Days
          </Button>
          <Button
            variant={dateRange === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange('all')}
          >
            All Time
          </Button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-4">
          {/* Live Updates Toggle */}
          <div className="flex items-center gap-2">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <Label htmlFor="auto-refresh" className="flex items-center gap-2 cursor-pointer">
              <Radio className={`h-4 w-4 ${autoRefresh ? 'text-green-500 animate-pulse' : 'text-muted-foreground'}`} />
              <span className="text-sm">Live</span>
            </Label>
            {autoRefresh && (
              <Badge variant="outline" className="text-green-500 border-green-500 text-xs">
                Active
              </Badge>
            )}
          </div>

          {/* Manual Refresh Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>

          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToCSV}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportToPDF}>
                <FileText className="h-4 w-4 mr-2" />
                Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Last Updated Indicator */}
      {lastUpdated && (
        <div className="text-xs text-muted-foreground text-right">
          Last updated: {format(lastUpdated, 'HH:mm:ss')}
          {isRefreshing && <span className="ml-2 text-primary">Refreshing...</span>}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Meetings</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalMeetings}</div>
            <p className="text-xs text-muted-foreground">
              {analytics.todayMeetings} today
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Participants</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalParticipants}</div>
            <p className="text-xs text-muted-foreground">
              Avg {analytics.avgParticipants} per meeting
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.avgDuration}m</div>
            <p className="text-xs text-muted-foreground">
              Per meeting
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalMessages}</div>
            <p className="text-xs text-muted-foreground">
              Across all meetings
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Trend Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.todayMeetings}</div>
            <p className="text-xs text-muted-foreground">meetings completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.weekMeetings}</div>
            <p className="text-xs text-muted-foreground">meetings completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.monthMeetings}</div>
            <p className="text-xs text-muted-foreground">meetings completed</p>
          </CardContent>
        </Card>
      </div>

      {webinarMetrics && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-primary" />
            <h3 className="font-semibold">Webinar Mode Insights</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Scheduled/Completed Webinars</CardTitle>
                <CardDescription>Total with webinar mode enabled</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{webinarMetrics.totalWebinars}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Avg Attendance</CardTitle>
                <CardDescription>Per webinar session</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{webinarMetrics.avgAttendance}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Avg Hand Raises</CardTitle>
                <CardDescription>Engagement per webinar</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{webinarMetrics.avgHandRaises}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Peak Participation</CardTitle>
                <CardDescription>{webinarMetrics.peakHourLabel}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{webinarMetrics.peakAttendance}</div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Recent Meetings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Meetings</CardTitle>
          <CardDescription>Latest completed meetings with details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {analytics.recentMeetings.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No meetings completed yet
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 text-sm font-medium">Meeting</th>
                      <th className="text-left p-2 text-sm font-medium">Duration</th>
                      <th className="text-left p-2 text-sm font-medium">Participants</th>
                      <th className="text-left p-2 text-sm font-medium">Messages</th>
                      <th className="text-left p-2 text-sm font-medium">Ended</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.recentMeetings.map((meeting) => (
                      <tr key={meeting.id} className="border-b">
                        <td className="p-2 text-sm">{meeting.title}</td>
                        <td className="p-2 text-sm">{meeting.duration_minutes}m</td>
                        <td className="p-2 text-sm">{meeting.total_participants}</td>
                        <td className="p-2 text-sm">{meeting.total_messages}</td>
                        <td className="p-2 text-sm">
                          {meeting.ended_at && format(new Date(meeting.ended_at), 'MMM d, HH:mm')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Meeting Trends - Area Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Meeting Trends
            </CardTitle>
            <CardDescription>Daily meeting activity over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics.dailyStats}>
                  <defs>
                    <linearGradient id="colorMeetings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => format(new Date(value), 'MM/dd')}
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="meetings" 
                    stroke="hsl(var(--primary))" 
                    fillOpacity={1} 
                    fill="url(#colorMeetings)"
                    name="Meetings"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Participant Growth - Line Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Participant Growth
            </CardTitle>
            <CardDescription>Daily participant count over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics.dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => format(new Date(value), 'MM/dd')}
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="participants" 
                    stroke="hsl(var(--chart-2))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--chart-2))', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                    name="Participants"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Combined Bar Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChartIcon className="h-5 w-5" />
              Daily Activity Comparison
            </CardTitle>
            <CardDescription>Meetings vs Participants per day</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => format(new Date(value), 'MM/dd')}
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="left"
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                  />
                  <Legend />
                  <Bar 
                    yAxisId="left"
                    dataKey="meetings" 
                    fill="hsl(var(--primary))" 
                    name="Meetings"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar 
                    yAxisId="right"
                    dataKey="participants" 
                    fill="hsl(var(--chart-2))" 
                    name="Participants"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
